import React, { useEffect, useState, useCallback } from 'react';
import styled from '@emotion/styled';

import { ComponentType, ComponentStyle, ProductClientSource } from '../../../types/types';
import { tryHandleThirdPartyCTA } from '../../../third-party/handle-third-party-cta';
import { removeHtmlFromString } from '../../../components/helpers';

import Benefits from '../../../components/Benefits';
import Cost from '../../../components/Cost';
import CTA from '../../../components/CTA';
import Documents from '../../../components/Documents'; // De-scoped
import Header from '../../../components/Header';
import Summary from '../../../components/Summary';
import Disclaimer from '../../../components/Disclaimer';
import Link from '../../../components/Link';

import { productComponentContainerPaddingStyle } from '../../../components/shared/styles';

import { Config, ErrorPanel, EventsManager, useParams, isNativeView, exitWebApp } from '@vm/omega-shared-component-webapp';

import { Product } from '../../../store/marketplace/types';
import { setUpNativeConfirm } from '../../../utils/setUpNativeCallback';

const CTAErrorRelativeContainer = styled.div({
  position: 'relative',
  width: '100%',
});

const CTAErrorAbsoluteContainer = styled.div(productComponentContainerPaddingStyle, {
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '100%',
  zIndex: 10,
});

type Props = {
  product: Product;
};

export const ProductPage: React.FC<Props> = ({ product }) => {
  const { params, pushParamsToHistory } = useParams();
  const [ctaDidError, setCtaDidError] = useState(false);

  useEffect(() => {
    if (ctaDidError) {
      window.scrollTo(0, 0);
    }
  }, [ctaDidError]);

  useEffect(() => {
    // set the parameters to the native handler function on each change of product
    if (product?.productId && product?.client?.id) {
      setUpNativeConfirm(product.productId, product.client.id);
    }
  }, [product]);

  useEffect(() => {
    const title = ['Marketplace'];
    if (product?.components) {
      const header = product.components.find(component => component.type === ComponentType.Header);
      if (header) {
        title.push(removeHtmlFromString(header.title));
      } else {
        const firstComponent = product.components[0];
        if (firstComponent) {
          title.push(removeHtmlFromString(firstComponent.title));
        }
      }
    }
    EventsManager.pageTitle.update(title);
  }, [product]);

  const onProductCtaClick = (cta: string) => {
    const clientSource = product?.client.source;
    switch (clientSource) {
      case ProductClientSource.VirginMoney: {
        pushParamsToHistory(params, Config.get('paths.product_application.root'), { productApplication: true }, false);
        break;
      }
      case ProductClientSource.ThirdParty: {
        if (!isNativeView()) {
          tryHandleThirdPartyCTA(product.client, cta, product.productId).catch(() => {
            setCtaDidError(true);
          });
        } else {
          if (product?.productId && product?.client?.id) {
            setUpNativeConfirm(product.productId, product.client.id);
          }
          exitWebApp(cta);
        }
        break;
      }
    }
  };

  const getComponents = () => {
    if (!product || !product.components) return null;
    return product.components.map((component, i) => {
      // Ensure component style is upper case
      component.style = component.style?.toUpperCase() as ComponentStyle;
      switch (component.type) {
        case ComponentType.Header:
          return <Header key={i} index={i} productComponent={component} />;
        case ComponentType.Benefits:
          return <Benefits key={i} index={i} productComponent={component} />;
        case ComponentType.Documents: // De-scoped
          return <Documents key={i} index={i} productComponent={component} />; // De-scoped
        case ComponentType.CTA:
          return <CTA key={i} index={i} productComponent={component} onCtaClick={onProductCtaClick} />;
        case ComponentType.Cost:
          return <Cost key={i} index={i} productComponent={component} />;
        case ComponentType.Summary:
          return <Summary key={i} index={i} productComponent={component} />;
        case ComponentType.Disclaimer:
          return <Disclaimer key={i} index={i} productComponent={component} />;
        case ComponentType.Link:
          return <Link key={i} index={i} productComponent={component} />;
      }
    });
  };

  return (
    <div>
      {ctaDidError ? (
        <CTAErrorRelativeContainer>
          <CTAErrorAbsoluteContainer>
            <ErrorPanel />
          </CTAErrorAbsoluteContainer>
        </CTAErrorRelativeContainer>
      ) : null}
      {getComponents()}
    </div>
  );
};
